/* eslint-disable react/display-name */
import { AnchorButton, Classes, H6, Intent, Tag } from '@blueprintjs/core';
import * as React from 'react';
import { DEFAULT_QUERY_BUILDER_SCHEMA, Text } from 'vet-bones/components';
import { QueryBuilderCopy } from 'vet-bones/utils/queryBuilder';

import {
  MAX_COMPETENCY_QUESTIONS,
  MAX_CSV_UPLOAD_BYTES,
  MAX_FILE_SIZE_IN_MB,
} from 'src/constants/limits';
import {
  DESIGNER_ARTIFACT_DEFINITIONS_LINK,
  PROJECT_SETTINGS_DOCUMENTATION_LINK,
  SSL_SETUP_DOCUMENTATION_LINK,
} from 'src/constants/links';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import {
  ProjectPropertyType,
  ProjectResourceType,
} from 'src/constants/project';
import { CompetencyQuestionEvaluationTag } from 'src/constants/voicebox';
import { LayoutAlgorithmType } from 'src/containers/ProjectDiagram/templates/extensions/layouts/types';
import { STARDOG_FEATURE_SUPPORT } from 'src/store/features/types';
import { VirtualGraphOption } from 'src/store/state/dataSources';
import { formatDateFromMs } from 'src/utils/formatDateFromMs';

const rulesQueryBuilderBase: Omit<QueryBuilderCopy, 'error'> = {
  body: {
    ...DEFAULT_QUERY_BUILDER_SCHEMA.copy.body,
    instance: {
      ...DEFAULT_QUERY_BUILDER_SCHEMA.copy.body.instance,
      valueTooltip:
        'To specify an instance, provide a set of valid IRIs enclosed in backticks ([`]) and comma-separated ([,])',
    },
  },
};

export const rulesIfQueryBuilder: QueryBuilderCopy = {
  ...rulesQueryBuilderBase,
  error: {
    ...DEFAULT_QUERY_BUILDER_SCHEMA.copy.error,
    classInstanceDefined:
      'Class instance is already defined. To continue with this rule, select a different class instance or property.',
    classModelDomainOrRange:
      'Class instances must be associated to the selected property. To continue with this rule, select a valid class instance.',
    classModelUndefined:
      'Class instances must be defined in the data model. To continue with this rule, select an existing class instance.',
    classVariableInvalid:
      'Class instances cannot be associated to instances in other AND NOT expressions. To continue with this rule, select a different class instance.',
    // not used for if block
    classPreviousInstance: '',
    classSubjectConnected:
      'Class instances must be associated with previous instances. To continue with this rule, connect this class instance to a previous instance.',
    propertyModelUndefined:
      'Properties must be defined in the data model. To continue with this rule, select an existing property.',
    propertyModelDomainOrRange:
      'Properties must be associated with the selected class. To continue with this rule, select a valid property.',
  },
};

export const rulesThenQueryBuilder: QueryBuilderCopy = {
  ...rulesQueryBuilderBase,
  error: {
    ...DEFAULT_QUERY_BUILDER_SCHEMA.copy.error,
    classInstanceDefined:
      'Class instance is already defined. To continue with this rule, select a different class instance or property.',
    classModelDomainOrRange:
      'Class instances must be associated to the selected property. To continue with this rule, select a valid class instance.',
    classModelUndefined:
      'Class instances must be defined in the data model. To continue with this rule, select an existing class instance.',
    classPreviousInstance:
      'Class instances must be defined in the "If" clause. To continue with this rule, select an existing class instance.',
    classSubjectConnected:
      'Class instances must be associated with previous instances. To continue with this rule, connect this class instance to a previous instance.',
    // not used for then block
    classVariableInvalid: '',
    propertyModelUndefined:
      'Properties must be defined in the data model. To continue with this rule, select an existing property.',
    propertyModelDomainOrRange:
      'Properties must be associated with the selected class. To continue with this rule, select a valid property.',
  },
};

export const components = {
  defaults: {
    advancedOptions: 'advanced options',
    alert: {
      ok: 'OK',
    },
    backButton: {
      back: 'Back',
    },
    confirmation: {
      title: 'Are you sure?',
      no: 'No',
      yes: 'Yes',
    },
    error: 'Something went wrong.',
    filter: 'Filter...',
    filterOrCreate: 'Filter or create...',
    itemSelectorCreate: (query: string) => `Create "${query}"`,
    noResults: 'No results.',
    prompt: {
      submit: 'OK',
      cancel: 'Cancel',
    },
  },
  appNavbar: {
    title: 'Stardog Designer',
    connectionButton: {
      basic: {
        tooltipHeader: 'Stardog Endpoint',
      },
      portal: {
        tooltipHeader: 'Stardog Cloud Account',
        home: 'Stardog Cloud',
      },
    },
  },
  browserAuthFailedDialog: {
    title: 'Browser Authentication Failed',
    message:
      'Could not connect to your endpoint using browser authentication. Please check your browser configuration.',
    homeButton: 'Stardog Cloud',
  },
  cards: {
    connection: {
      label: 'Connect to Stardog',
      placeholder: '',
    },
    model: {
      label: 'Select a data model',
      placeholder: 'Select a data model',
      selected: (models: string[]) =>
        (models.length > 1 ? `${models.length} selected` : models[0]) || '',
    },
  },
  classSelector: {
    placeholder: 'Select class(es)...',
    singularPlaceholder: 'Select a class...',
  },
  colorPicker: {
    title: 'Color',
    tooltip: 'Change color',
  },
  databaseSelector: {
    placeholder: 'Select a database',
    creatablePlaceholder: 'Select or create',
    errorMessages: {
      startsWith:
        'The database name must start with an alphabet character ([A-Za-z]).',
      catalog:
        'This database already exists. Please enter a unique database name.',
      characters: (chars: string[]) =>
        `The database name is invalid. "${chars.join(',')}" is not allowed.`,
    },
  },
  dialogConnectionTile: {
    connection: {
      proceedButton: 'Proceed',
      username: (uname?: string) => `username: ${uname || ''}`,
      otherConnection: 'Want to use a different endpoint? ',
      selectHere: 'Select here',
    },
    insufficientPrivilegesCreateOrReadDataSource:
      'Insufficient privileges to create or read data sources.',
    insufficientPrivilegesCreateDataSource:
      'Insufficient privileges to create new data sources.',
    insufficientPrivilegesReadDataSource:
      'Insufficient privileges to read data sources.',
    insufficientPrivilegesImportModel:
      'Insufficient privileges to import a data model.',
    insufficientPrivilegesUploadModel:
      'Insufficient privileges to upload a data model.',
  },
  cloudStorage: {
    outOfSyncDialog: {
      title: 'Cloud Storage Conflict',
      message: (
        localProjectName: string,
        remoteProjectUpdatedAt: number,
        localProjectUpdatedAt: number,
        isLaunchpad: boolean
      ) => {
        const prettyLocalUpdatedAt = new Date(
          localProjectUpdatedAt || 0
        ).toLocaleString();
        const prettyRemoteUpdatedAt = new Date(
          remoteProjectUpdatedAt || 0
        ).toLocaleString();
        return (
          <>
            The local and {isLaunchpad ? 'account' : 'cloud'} versions of{' '}
            <strong>{localProjectName}</strong> have conflicting changes.
            <br />
            <br />
            Local version last updated:
            <br />
            {prettyLocalUpdatedAt}
            <br />
            <br />
            {isLaunchpad ? 'Account' : 'Cloud'} version last updated:
            <br />
            {prettyRemoteUpdatedAt}
            <br />
          </>
        );
      },
      yesPrompt: 'Retain both versions',
      noPrompt: (isLaunchpad: boolean) =>
        `Overwrite  ${
          isLaunchpad ? 'account' : 'cloud'
        } version with local changes`,
    },
  },
  dropzone: {
    browse: 'Browse',
    drag: 'Drag files to upload',
  },
  genericPropertiesEditor: {
    validation: {
      duplicatedNameTooltip: 'Options may only be specified once',
      reservedNameTooltip: 'This is a reserved option that can be set above',
      missingValueTooltip: 'A value is required',
    },
  },
  home: {
    search: 'Search projects',
    new: 'New',
    newProject: 'New Project',
    importProject: 'Import project',
    orderBy: 'Order by',
    cards: {
      deleteMenuItem: 'Delete Project',
      duplicateMenuItem: 'Duplicate',
      projectStoredLocally: 'Project stored locally',
      storeLocallyMenuItem: 'Store Locally',
      storeCloudMenuItem: (isLaunchpad: boolean) =>
        `Promote to ${isLaunchpad ? 'account' : 'cloud'} storage`,
      confirmationDialogContent: (isCloud: boolean, isLaunchpad: boolean) => {
        let destination = 'store your project locally?';
        if (!isCloud) {
          destination = 'promote your project to cloud storage?';
        } else if (isLaunchpad) {
          destination = 'promote your project to account storage?';
        }
        return `Are you sure you want to ${destination}`;
      },
    },
    voicebox: {
      headerFirst: 'Introducing',
      headerSecond: 'Stardog Voicebox',
      body: "Voicebox is Stardog's LLM for accelerating the development, maintenance, and use of a knowledge graph. Strike up a conversation about what type of data model you want built and Voicebox will make it happen!",
      button: 'Start the conversation',
    },
    noProjects: 'No Projects',
    noProjectsText: (
      <Text className={Classes.TEXT_LARGE}>
        You have not created any projects, click on{' '}
        <strong>&quot;New Project&quot;</strong> to get started
      </Text>
    ),
  },
  createProjectDialog: {
    title: 'New Project',
    name: 'Project name',
    namePlaceholder: 'Enter project name',
    cloudStorage: 'Use Cloud Storage',
    select: 'Select how to start your project',
    voiceboxTitle: 'Voicebox',
    voiceboxDescription: 'Build via Voicebox',
    blankTitle: 'Blank Canvas',
    blankDescription: 'Build from scratch',
    importTitle: 'Import a data model',
    importDescription:
      'Import an existing data model to edit, extend, or map to',
    resourceTitle: 'Connect a resource',
    resourceDescription:
      'Leverage technical metadata from data connections to map and model',
    validation: {
      name: 'Name is required',
    },
  },
  createProjectOverlay: {
    steps: {
      createProject: 'Create Project',
      projectScoping: 'Project Scoping',
      competencyQuestions: 'Competency Questions',
      dataConnection: 'Data Connection',
    },
    projectScopingPanel: {
      title: "Let's get started by defining your ",
      titleSpan: 'project',
      subtitle: 'Start by giving your project a title and brief description.',
      helpText:
        'The collected data will help us generate better questions and data suggestions.',
      titleLabel: 'Title',
      namePlaceholder: 'What is your project about?',
      descriptionLabel: 'Description',
      descriptionPlaceholder:
        'Describe the goals and the main focus of this project...',
      titleRequiredTooltip: 'Title is required.',
    },
    competencyQuestionsPanel: {
      title: 'What ',
      titleSpan: 'questions',
      titleContinue: ' can Stardog answer from your data?',
      subtitle: 'These will be answered by the Knowledge Graph.',
      subtitleSecond:
        'Aim for 5-20 well-formed questions for the best results.',
      tipsTitle: 'Tips to write\nbetter questions',
      tips: [
        'Be specific and context-rich.',
        'Focus on relationships, not just keywords.',
        'Align questions with business/personal goals or decisions.',
      ],
      myQuestions: 'My questions',
      addQuestion: 'Add question',
      addQuestionDisabled: `Limit: ${MAX_COMPETENCY_QUESTIONS} questions per project`,
      generateQuestions: 'Generate additional questions',
      questionPlaceholder: 'Add question',
    },
    dataConnectionPanel: {
      title: 'Provide ',
      titleSpan: 'data',
      subtitle:
        'Based on your questions, Stardog needs your data related to following:',
      concepts: 'Concepts',
      generateMockData: 'Generate model with mock data',
      dropzone: {
        dragText: 'Drag and drop CSV files here',
        supportText: 'or click to browse',
      },
      myResources: 'My Resources',
      addData: 'Add data',
      generateModel: 'Generate Model',
      generatingModel: 'Generating model from your data...',
      addDataDrawer: {
        title: 'Add Data',
        csvSection: 'Upload CSV',
        vgSection: 'Connect to a data source',
        vgNotConnected: 'Connect to Stardog to access data sources',
        vgLoading: 'Loading data sources...',
      },
    },
  },
  fileUploadPreviewDialog: {
    title: 'Resource Preview',
    revertButton: 'Revert',
    saveButton: 'Save',
    nextButton: 'Next',
  },
  invalidMappings: {
    dialog: {
      cancel: 'Cancel',
      message:
        'The following mappings will not be included as they do not include a valid primary identifier',
      proceed: 'Proceed',
      title: 'Invalid mappings',
    },
    validation: {
      primaryIdentifier: (name: string) =>
        `mapping "${name}" does not have a valid "primaryIdentifier"`,
      sourceClassIri: (iri: string) =>
        `mapping for "${iri}" does not have a valid "sourceClassIri"`,
      sourcePrimaryIdentifier: (iri: string) =>
        `mapping for "${iri}" does not have a valid "sourcePrimaryIdentifier"`,
      targetClassIri: (iri: string) =>
        `mapping for "${iri}" does not have a valid "targetClassIri"`,
      targetPrimaryIdentifier: (iri: string) =>
        `mapping for "${iri}" does not have a valid "targetPrimaryIdentifier"`,
      to: (name: string) => `mapping "${name}" has no "to"`,
      fromOrTo: (iri: string) => `mapping for "${iri}" has no "from" or "to"`,
    },
  },
  project: {
    competencyQuestions: {
      addQuestionDisabled: `Limit: ${MAX_COMPETENCY_QUESTIONS} questions per project`,
      cardTitle: 'Question Summary',
      title: 'Questions',
      edit: 'Edit',
      evaluate: 'Re-evaluate',
      delete: 'Delete',
      moreDetails: 'More details',
      placeholder: 'Add question',
      tags: {
        [CompetencyQuestionEvaluationTag.AMBIGUITY]: 'Question is unclear.',
        [CompetencyQuestionEvaluationTag.MISSING_DATA]:
          'No supporting data mapped.',
        [CompetencyQuestionEvaluationTag.MISSING_MODEL]:
          'Concepts are not defined in the data model.',
      },
    },
    resources: {
      title: 'Resources',
      filter: 'Search resources...',
      delete: 'Delete',
      viewPreview: 'View Preview',
    },
    mappings: {
      title: 'Mappings',
      delete: 'Delete',
      deleteConfirmation: 'Are you sure you want to delete this mapping?',
      resourceSuggestionsDisabled: `Resource suggestions require Stardog Knowledge Graph endpoint version ${STARDOG_FEATURE_SUPPORT.autoMappingMultipleTables.minVersion}+`,
      settings: 'Settings',
    },
    models: {
      title: 'Data Models',
      filter: 'Search data models...',
      delete: 'Delete',
      hide: (isHidden: boolean) => (isHidden ? 'Show' : 'Hide'),
      hiddenTarget: 'Data model used for new concepts cannot be hidden',
      hiddenTooltip: 'This data model is hidden from view',
      refresh: 'Refresh',
      refreshDisabledDraft:
        'To refresh data models, please first accept or discard the changes to your project',
      rename: 'Rename',
      settings: 'Manage settings',
      target: 'Use for new concepts',
    },
    navbar: {
      back: 'Back',
      export: 'Export',
      exportHover: 'Export data model, mapping, and project definition files',
      insufficientPrivilegesPublish:
        'Insufficient privileges to publish project.',
      mapping: 'Mapping',
      model: 'Data Model',
      publish: 'Publish',
      publishHover: 'Publish to Stardog Endpoint',
      settings: 'Settings',
      saveState: {
        saved: (isLaunchpad: boolean) =>
          isLaunchpad ? 'Saved to Account' : 'Saved to Cloud',
        saving: 'Saving...',
        offline: 'Working Offline',
        locallySaved: 'Saved Locally',
      },
    },
    propertyType: {
      buttonText: (type: ProjectPropertyType) => {
        const types: Record<ProjectPropertyType, string> = {
          RDFS: 'Yes',
          SCHEMA_ORG: 'No',
          CUSTOM: 'Custom per property',
        };
        return types[type];
      },
      info: (
        <>
          Shared properties (relationships and attributes) can imply inference
          class types.{' '}
          <div className="sd-tooltip-footer">
            <AnchorButton
              href={PROJECT_SETTINGS_DOCUMENTATION_LINK}
              intent={Intent.PRIMARY}
              outlined
              rel="noreferrer"
              small
              target="_blank"
            >
              Learn more
            </AnchorButton>
          </div>
        </>
      ),
      label: 'Infer class types based on shared properties',
    },
    resourceTypes: {
      fileUpload: 'file upload',
      virtualGraph: 'virtual graph',
    },
  },
  projectDiagram: {
    attributeDrawer: {
      title: 'Attribute Detail',
      name: 'Attribute Name',
      dataType: 'Data Type',
      associations: 'Associations',
      sensitivePropertyGroups: 'Sensitive Property Groups',
      createSPG: 'Create',
      filter: 'Filter',
      nameColumn: 'Name',
      attributeColumn: 'Other attributes',
      moreCount: (count: number) =>
        count > 2 ? `, and ${count - 2} more` : '',
    },
    attributesTable: {
      createRule: 'Create',
      label: 'Attributes',
      missingAttribute: 'Attribute is required',
      missingDatatypes: 'Data type(s) is required',
      missingName: 'Attribute name is required',
      nameColumn: 'Attribute Name',
      noType: 'No type',
      rule: 'Rule',
      typeColumn: 'Data Type',
      updateButton: 'Update',
    },
    classDrawer: {
      createTitle: 'Create Class',
      title: 'Class Detail',
      name: 'Class Name',
      nameWithAttributes: 'Class Name - Attributes',
      childClasses: 'Child Classes',
      parentClasses: 'Parent Classes',
      tabs: {
        basic: 'Basic',
        hierarchy: 'Hierarchy',
        relationships: 'Relationships',
        attributes: 'Attributes',
        geospatial: 'Geospatial',
      },
      geospatialTable: {
        contentColumn: 'Content',
        createStaticText: (text: string) => `Static Value "${text}"`,
        invalidWarningCallout:
          'Some attributes have incompatible datatypes and may not display correctly.',
        invalidWarningTooltip:
          'This attribute has an incompatible datatype and may not display correctly.',
        mapDetailColumn: 'Map Detail',
        labels: {
          location: 'Point',
          latitude: 'Latitude',
          longitude: 'Longitude',
          nodeSize: 'Node Size',
          popupTitle: 'Popup Title',
          popupContent: 'Popup Content',
          tooltipTitle: 'Tooltip Title',
          tooltipContent: 'Tooltip Content',
        },
        validation: {
          location: (value: string) => `"${value}" is not a valid Point`,
        },
      },
      relationshipsTable: {
        label: 'Relationships',
        missingRelationship: 'Relationship is required',
        missingDirection: 'Direction is required',
        missingName: 'Relationship name is required',
        nameColumn: 'Relationship Name',
        directionColumn: 'Direction',
        directionIncoming: 'Incoming',
        directionOutgoing: 'Outgoing',
      },
    },
    contextMenu: {
      delete: 'Delete',
      deleteResource: 'Delete resource',
      deleteMultiple: 'Delete objects',
      edit: 'Edit',
      mapping: 'Mapping',
      mappings: 'Mappings',
      newMapping: 'New Mapping',
      preview: 'View preview',
      seeDetails: 'See details',
      settings: 'Settings',
    },
    createDialogs: {
      createButton: 'Create',
      errors: {
        noName: 'A name is required.',
        notUnique:
          'The name you have entered is not unique in your project. Please enter a unique name.',
      },
    },
    createDrawers: {
      cancelButton: 'Cancel',
      createButton: 'Create',
    },
    editableIri: {
      showIri: 'Show IRI',
      hideIri: 'Hide IRI',
      modelIriNamespace: '[DATA MODEL NAMESPACE]',
      modelIriPlaceholder: '[DATA MODEL NAMESPACE]modelConcept...',
      validationLabelRequired: 'Name is required.',
      validationIriRequired: 'IRI is required.',
      validationIriStartsWith:
        'The IRI must start with an alphabet character ([A-Za-z]).',
      validationBaseIriSemicolon:
        'The base IRI must end with a semicolon ([:]).',
      validationIriSemicolon: 'The IRI must include a semicolon ([:]).',
      validationIriCharacters: (chars: string[]) =>
        `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
      validationIriUnique:
        'This IRI already exists. Please enter a unique IRI.',
      vgNameValidationError:
        'Data Source names must be unique and start with an alpha character followed by alphanumeric characters, underscores, or dashes.',
    },
    entityDrawer: {
      description: 'Description',
      models: 'Data Models',
      modelsRemoveDisabled: 'At least one data model is required.',
    },
    entityDrawerButtons: {
      add: 'Add',
      attributeEditDisabled:
        'Inherited attributes cannot be edited. The attribute must be edited from the parent class.',
      attributeUnlinkDisabled:
        'Inherited attributes cannot be unlinked. The attribute must be unlinked from the parent class.',
      edit: 'Edit',
      relationshipEditDisabled:
        'Inherited relationships cannot be edited. The relationship must be edited from the parent class.',
      relationshipUnlinkDisabled:
        'Inherited relationships cannot be unlinked. The relationship must be unlinked from the parent class.',
      unlink: 'Unlink',
      update: 'Update',
      remove: 'Delete',
    },

    mapping: {
      createMessage: 'Mapping name',
      defaultLabel: (idx: number) => `Mapping ${idx + 1}`,
      new: 'New Mapping',
      mappingTarget: 'Mapping target',
      mappingTargetHelp: 'Leave blank to manually connect mapping link',
    },
    modelConceptAttribute: {
      createMessage: 'Attribute name',
      new: 'New Attribute',
    },
    modelConceptClass: {
      createMessage: 'Class name',
      new: 'New Class',
    },
    modelConceptRelationship: {
      createMessage: 'Relationship name',
      new: 'New Relationship',
    },
    modelDeletionWarning: {
      cancel: 'Cancel',
      message: (
        modelName: string,
        classes: number,
        relationships: number,
        attributes: number,
        rules: number
      ) => (
        <>
          <H6>{`Deleting ${modelName} will also delete all model concepts only associated with this data model.`}</H6>
          <ul>
            {classes ? <Text>Classes: {classes}</Text> : null}
            {relationships ? <Text>Relationships: {relationships}</Text> : null}
            {attributes ? <Text>Attributes: {attributes}</Text> : null}
            {rules ? <Text>Rules: {rules}</Text> : null}
          </ul>
        </>
      ),
      proceed: 'Proceed',
    },
    modelList: {
      table: {
        columnPlaceholder: 'Search',
        columns: 'Columns',
        hide: 'Hide column',
        sortBooleanAsc: 'Sort last',
        sortBooleanDesc: 'Sort first',
        sortNumberAsc: 'Sort ascending',
        sortNumberDesc: 'Sort descending',
        sortTextAsc: 'Sort ascending',
        sortTextDesc: 'Sort descending',
        tablePlaceholder: 'Search',
      },
      tabs: {
        classes: 'Classes',
        relationships: 'Relationships',
        attributes: 'Attributes',
      },
      classes: {
        emptyState: {
          title: 'No Classes',
          description: 'Your project does not contain any classes.',
          button: 'Add class',
        },
        name: 'Class Name',
        iri: 'IRI',
        attributes: 'Attributes',
      },
      relationships: {
        emptyState: {
          title: 'No Relationships',
          description: 'Your project does not contain any relationships.',
          button: 'Add relationship',
        },
        name: 'Relationship Name',
        iri: 'IRI',
        domains: 'Sources',
        ranges: 'Targets',
      },
      attributes: {
        emptyState: {
          title: 'No Attributes',
          description: 'Your project does not contain any attributes.',
        },
        name: 'Attribute Name',
        iri: 'IRI',
        ranges: 'Data Types',
        domains: 'Associated Classes',
      },
    },
    modelPopover: {
      label: 'Model: ',
    },
    modificationWarning: {
      attributesMessage: (className: string) =>
        `Deleting ${className} will also delete the following attributes:`,
      cancel: 'Cancel',
      rulesMessage:
        'Making this modification will cause the following rule(s) to be incomplete:',
      proceed: 'Proceed',
    },
    navbar: {
      addClass: 'Add class',
      addMapping: 'Add mapping',
      addRelationship: 'Add relationship',
      deleteSelection: 'Delete selection',
      modelGraph: 'Graph',
      modelList: 'List',
      redo: 'Redo',
      redrawLayout: 'Redraw Layout',
      rules: 'Rules',
      search: 'Search',
      searchPlaceholder: 'Search',
      undo: 'Undo',
      voicebox: 'Voicebox',
    },
    relationshipDrawer: {
      createTitle: 'Create Relationship',
      title: 'Relationship Detail',
      name: 'Relationship Name',
      domain: 'Source',
      range: 'Target',
    },
    resourceDrawer: {
      fieldName: 'Field Name',
      fields: 'Fields',
      mapping: 'Mapping',
      mappingName: 'Mapping Name',
      newMapping: 'New Mapping',
      removeMapping: 'Remove Mapping',
      target: 'Target',
      title: 'Resource Detail',
      type: 'Type',
      updateDate: 'Update Date',
    },
    sensitivePropertyGroupDrawer: {
      createTitle: 'Create Sensitive Property Group',
      title: 'Sensitive Property Group Detail',
      name: 'Name',
      new: 'New Sensitive Property Group',
      attributes: 'Attributes',
      validation: {
        name: {
          required: 'Name is required',
          characters: (chars: string[]) =>
            `The sensitive property group name is invalid. "${chars.join(
              ','
            )}" is not allowed.`,
          unique:
            'This sensitive property group already exists. Please enter a unique name.',
        },
      },
    },
  },
  projectMapping: {
    emptyState: {
      title: 'No mapping selected',
      description: 'Select a mapping from the sidebar on the left',
    },
    annotations: {
      button: 'Annotations',
      label: 'Label',
      labelHelp:
        'Label annotations are used throughout Stardog as the display name for an instance. When no label is set, the primary identifier value will be displayed instead.',
    },
    attributes: {
      edit: 'Edit Attributes',
      inheritedFrom: 'Inherited from',
      section: 'Attributes',
    },
    filter: {
      directAttributes: 'Attributes',
      inheritedAttributes: 'Inherited Attributes',
      directRelationships: 'Relationships',
      inheritedRelationships: 'Inherited Relationships',
      iconLabel: 'Filter Attributes',
      selectAll: 'Select All',
      clear: 'Clear',
    },
    menu: {
      duplicateMapping: 'Duplicate mapping',
      removeMapping: 'Remove mapping',
    },
    primaryIdentifier: {
      label: 'Primary Identifier',
      help: 'A primary identifier is used to uniquely define entities for your mapping target. If a mapping target has entities from multiple sources, the primary identifiers across sources should match to avoid duplicates.',
      fieldRequired:
        ' Primary identifiers require at least one field to be used',
      inputPlaceholder: 'Field or static text',
      invalidCharacter: 'Special characters are not supported',
      invalidSpace: 'Spaces are not supported',
      preview: 'Preview: ',
      emptyPreview: 'Preview will be shown once at least one field is selected',
      staticText: (query: string) => `Static Text "${query}"`,
      showIri: 'Show base IRI',
      hideIri: 'Hide base IRI',
      editedIri: 'An edited base IRI cannot be hidden',
      dataIriNamespace: '[INSTANCE DATA NAMESPACE]',
      dataIriPlaceholder: (classLocalName: string) =>
        `[INSTANCE DATA NAMESPACE]${classLocalName}:`,
    },
    relationships: {
      classSelector: (isSource: boolean) =>
        `${isSource ? 'Source' : 'Target'} Class`,
      identifierType: (isSource: boolean) =>
        `${isSource ? 'Source' : 'Target'} Identifier`,
      inheritedFrom: 'Inherited from',
      mapping: 'Mapping',
      mappedIn: 'mapped in',
      alsoMappedIn: `Mapped in`,
      newMapping: 'New mapping',
      noMappingItems: 'There are no existing mappings for this class.',
      section: 'Relationships',
      source: 'Source',
      target: 'Target',
      sourceWarning:
        'The field used to denote the primary identifier for the source class differs from the field used to denote the primary identifier for this same class from the same project resource. Typically the same identifier is used.',
      targetWarning:
        'The field used to denote the primary identifier for the target class differs from the field used to denote the primary identifier for this same class from the same project resource. Typically the same identifier is used.',
    },
    resourceTypeLabel: (type?: ProjectResourceType) => {
      if (!type) {
        return '';
      }

      const displayType =
        type === ProjectResourceType.FILE_UPLOAD
          ? 'Type: CSV'
          : 'Type: Virtual Graph';
      return displayType;
    },
    suggestions: {
      button: 'Suggest Mapping',
      disabled:
        'Suggestions require Stardog Knowledge Graph endpoint version 8.1.0+',
      disabledByAiMappingAssist:
        'Mapping suggestions are disabled. Enable AI mapping assist in project settings to use this feature.',
      score: 'Score',
      action: 'Action',
      low: 'LOW',
      medium: 'MEDIUM',
      high: 'HIGH',
    },
  },
  projectMappingSettingsDrawer: {
    advancedSettings: 'Advanced Settings',
    classTypeHelp:
      'Mapping will include a declaration that all instances are of the mapped type. Failure to set a type for an instance will result in poor Voicebox and Explorer experiences.',
    mappingName: 'Mapping Name',
    reset: 'Reset',
    setClassType: 'Set class type',
    title: 'Mapping Settings',
    uniqueKeySets: 'Unique key set',
    uniqueKeySetsHelp:
      'Used to identify the columns that uniquely identify each row from the resource. Not needed if the columns are already identified as a primary key. Note that rows that contain a null (or empty value if csv.skip.empty is true) for any of the unique-key columns will not be loaded.',
    uniqueKeySetsPlaceholder: 'Select fields...',
    insufficientPrivilegesUniqueKeySets:
      'Insufficient privileges to publish unique key sets.',
  },
  projectEndpointDialog: {
    title: 'Confirm Project Endpoint',
  },
  projectModelDialog: {
    title: 'Add a data model',
    addButton: 'Add',
    backButton: 'Back',
    localModelPanel: {
      name: 'Data Model',
      nameExampleText: 'Data model name',
      namespace: 'Data Model Namespace',
      namespaceExampleText: 'urn:model:example',
    },
    modelTypePanel: {
      browseFiles: 'Browse Files',
      local: 'Define',
      localSubtitle: 'Define a new data model',
      import: 'Import',
      proceedButton: 'Proceed',
      upload: 'Upload',
      uploadSubtitle: 'Drag and drop a .ttl file',
    },
    renameModelDialog: {
      submit: 'Rename',
      title: 'Rename Data Model',
    },
    uploadModelPanel: {
      fileUpload: 'File upload',
      name: 'Data Model',
      nameExampleText: 'Data model name',
      namedGraph: 'Named Graph',
    },
    validation: {
      comboModelContainsExtraGraphs: (modelNames: string[]) =>
        `${modelNames.join(', ')} ${
          modelNames.length > 1 ? 'are combo models' : 'is a combo model'
        } containing one or more named graphs which are not defined as data models. Only data models will be imported.`,
      largeModelLimit:
        'Importing this data model may impact performance due to the number of concepts and their connectivity. Using a subset is recommended to preserve responsiveness.',
      modelAlreadyExists: 'Data model name already exists in the database',
      namedGraphAlreadyExists: 'Named graph IRI already exists in the database',
      nameInvalidData: 'Data model name cannot be "data"',
      nameRequired: 'Data model name is required',
      namespaceRequired: 'Namespace is required',
      namespaceStartsWith:
        'The IRI must start with an alphabet character ([A-Za-z]).',
      namespaceSemicolon: 'The IRI must include a semicolon ([:]).',
      namespaceChars: (chars: string[]) =>
        `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
      specialNamedGraphNotAllowed:
        'Models cannot be added to special named graphs',
    },
  },
  projectResourceDialog: {
    title: 'Add a new project resource',
    backButton: 'Back',
    cancelButton: 'Cancel',
    createAndMapButton: 'Create & Map',
    createButton: 'Create',
    saveButton: 'Save',
    dataSourceTypeSelectionPanel: {
      noDataSources: 'No data sources have been added to this Stardog endpoint',
      sampleDataBanner: 'SAMPLE DATA',
    },
    addDataSourcePanel: {
      title: 'Select tables',
      addButton: 'Add',
    },
    resourceTypePanel: {
      titleAdd: 'Add data',
      titleUpload: 'Upload data',
      fileUpload: {
        dragText: 'Click or drag CSV files here to upload.',
        supportText: 'Supports CSV format files up to 2 GB.',
        button: 'Choose files',
      },
      dataSourceSection: {
        title: 'Connect to a data source',
        searchPlaceholder: 'Search connections',
      },
      projectResourceCard: {
        description: (columnCount: number, rowCount: number, columns: string) =>
          `The dataset contains ${columnCount} columns and ${rowCount} rows. Detected columns include ${columns}.`,
        viewPreview: 'View preview',
        removeFile: 'Remove file',
      },
      vgResourceCard: {
        description: (columnCount: number, rowCount: number, columns: string) =>
          `The table contains ${columnCount} columns and ${rowCount} rows. Columns include ${columns}.`,
        removeTable: 'Remove table',
        customQueryDefaultName: 'Custom Query',
      },
      dataSourceHeaderCard: {
        selectData: 'Select data',
        addMore: 'Add data +',
        removeAll: 'Remove all',
      },
    },
    fileUploadPanel: {
      title: 'Resource preview',
      file: 'File',
      fileNotFound: 'File not found',
      resourceProperties: 'Resource Properties',
      lastUpdated: (date: string) => `Last updated: ${date}`,
      mayNotReflectFullData: 'May not reflect full data',
      fileNamePlaceholder: 'Enter resource name',
      fillAllProperties: 'Please fill in all resource properties',
      mappingWarning: 'Invalid mappings will be removed',
    },
    projectDataSourceCreationPanel: {
      advancedOptions: (count: number) => `Advanced Options (${count})`,
      dataSourceNameLabel: 'Data Source Name',
      invalidDataSourceProperties: (names: string[]) =>
        names.find((name) => name === '') !== undefined
          ? 'Property keys cannot be blank.'
          : `Invalid values for properties: ${names.join(', ')}`,
      missingDataSourceProperties: (names: string[]) =>
        `Missing values for properties: ${names.join(', ')}`,
      name: {
        required: 'Data source name is required',
        startsWith:
          'The data source name must start with an alphabet character ([A-Za-z]).',
        characters: (chars: string[]) =>
          `The data source name is invalid. "${chars.join(
            ','
          )}" is not allowed.`,
        unique:
          'This data source already exists. Please enter a unique data source name.',
      },
      propertyLabels: {
        ENDPOINT: 'Endpoint',
        USERNAME: 'Username',
        PASSWORD: 'Password',
      },
    },
    projectFileUploadPanel: {
      closeTabButton: 'Close Tab',
      form: {
        duplicateHeaders:
          'The uploaded file contains duplicate values in the first row. Using headers is not supported for duplicate values.',
        escape: 'Escape character',
        header: 'Has header',
        name: 'Resource name',
        namelessTabLabel: (num: number) => `File ${num}`,
        path: 'Source',
        preview: 'Preview',
        previewRowCount: (rowCount: number, allResultsShown: boolean) =>
          allResultsShown ? `${rowCount} records` : `${rowCount} samples`,
        replace: 'Replace',
        resourceProperties: 'Resource Properties',
        quote: 'Quote character',
        separator: 'Separator character',
        skipEmpty: 'Skip empty values',
      },
      errors: {
        empty: 'No data',
        details: 'Details',
        title: 'Failed to parse row:',
        row: 'Row',
      },
      statistics: {
        distinct: (value: number) =>
          `${value % 1 === 0 ? value : value.toFixed(2)}% distinct`,
        records: (value: number) => `${value} record${value === 1 ? '' : 's'}`,
      },
      validation: {
        empty: 'Cannot upload CSV without data',
        errors: 'Cannot upload CSV with errors',
        escape: 'Escape character is required',
        name: 'Data Source name is required',
        quote: 'Quote character is required',
        separator: 'Separator character is required',
        errorWithName: (error: string, tabName: string) =>
          `${error} (${tabName})`,
      },
      warningTitle:
        'Editing a project resource may result in existing mappings becoming invalid',
    },
    projectVirtualGraphPanel: {
      form: {
        addQuery: 'Add Query',
        runQuery: 'Run',
        name: 'Resource name',
        pasteQuery: 'Paste query',
        preview: 'Preview',
        previewRowCount: (rowCount: number, allResultsShown: boolean) =>
          allResultsShown ? `${rowCount} records` : `${rowCount} samples`,
        resourceScope: 'Resource Scope',
        resourceProperties: 'Resource Properties',
        dataSource: 'Data Source',
        replace: 'Replace',
        tablesTooltip: {
          noTables:
            'There are no tables to select from. Contact your system admin to ensure the data source metadata is up to date.',
          noPermissions:
            'You are not authorized to see data source metadata. Contact your system admin to ensure you have the correct user privileges.',
        },
      },
      customQuery: 'Build custom query',
      preview: {
        clickToRefresh: 'Refresh preview',
        errors: {
          badQueryOneError:
            'An error was encountered while attempting to query the data source',
          badQueryMultipleErrors:
            'Multiple errors were encountered while attempting to query the data source',
          badQueryDetails: 'Details',
          emptyRefresh:
            'Attempting to refresh returned no results; retaining data from last update',
          previewEmpty:
            'There are no cached results for this resource. Refresh to view the data preview. The preview will remain empty if the resource scope has no data.',
        },
        lastUpdated: (timestamp: number) =>
          `Last updated: ${formatDateFromMs(timestamp)}`,
        validateToPreview: 'Run query to see preview',
        selectToPreview: 'Select a table to see a preview',
      },
      resourceProperties: {
        [VirtualGraphOption.SQL_QUOTING_CONVENTION]: {
          label: 'SQL Quoting Convention',
          description:
            'If set to `NATIVE`, R2RML views (using `rr:sqlQuery`) will be parsed using the DB-native identifier quoting convention. For example, MySQL queries will be parsed treating backtick as the identifier quote character. If set to `ANSI`, the ANSI SQL convention of treating a double quote as the identifier quote character will be used instead.',
        },
        [VirtualGraphOption.SQL_FUNCTIONS]: {
          label: 'SQL Function Names',
          description:
            'A comma-separated list of SQL function names to register with the parser. If an R2RML view (using `rr:sqlQuery`) fails to parse, this option can be set to allow use of non-standard functions.',
        },
        [VirtualGraphOption.PERCENT_ENCODE]: {
          label: 'Percent Encode IRI Template Strings',
          description:
            'Should IRI template strings be percent-encoded to be valid IRIs?',
        },
        advancedProperties: (count: number) => `Advanced Properties (${count})`,
      },
    },
  },
  projectRuleDetailDrawer: {
    title: 'Rule Detail',
    name: 'Rule Name',
    newRule: 'New Rule',
    ifClause: 'When the following conditions are met:',
    thenClause: 'Then infer:',
    classifyProperty: 'Classify As',
    delete: 'Delete',
    cancel: 'Cancel',
    reasoningWarning:
      'Your rule uses patterns that are only available when enabling the Stride reasoner. If the Stride reasoner is not enabled, this rule will be ignored.',
    ruleBuilderIncompleteTooltip:
      'Your Text Editor rule uses patterns that are not supported by the Rule Builder',
    ruleBuilderIncompleteCallout:
      'Rule Builder display is incomplete. Any edits in the Rule Builder view will overwrite the Text Editor rule.',
    ruleBuilderMultipleRulesError:
      'Multiple rules definitions cannot be modified in Rule Editor',
    ruleBuilderTabName: 'Rule Builder',
    submit: 'Save',
    textEditorMultipleRulesError:
      'Multiple instances in one "In" cannot be modified text edits',
    textEditorTabName: 'Text Editor',
    validation: {
      nameRequired: 'Rule name is required',
      incomplete: 'Please complete the highlighted criteria',
    },
  },
  projectRuleOverviewDrawer: {
    emptyState: {
      description:
        'Define inference rules to define capture business and domain rules in your data model.',
      title: 'Inference Rules for explainable AI',
    },
    title: 'Inference Rules',
    createRule: 'Create Rule',
    invalidRule: 'Incomplete Rule',
  },
  projectSettingsDrawer: {
    title: 'Project Settings',
    name: 'Project Name',
    description: 'Project Description',
    modelNamespace: 'Data Model Namespace',
    namespace: (modelName: string) => `${modelName} Namespace`,
    dataNamespace: 'Instance Data Namespace',
    modelSettings: 'Data Model Settings',
    customPropertyTypeWarning:
      'This change will modify your data model and overwrite your existing property settings.',
    cancel: 'Cancel',
    submit: 'Save',
    layout: {
      layoutLabel: 'Layout',
      [LayoutAlgorithmType.D3_FORCE_LAYOUT]: 'Force-directed',
      [LayoutAlgorithmType.GO_JS_VERTICAL_LAYERED_DIGRAPH_LAYOUT]:
        'Vertical Tree',
      [LayoutAlgorithmType.GO_JS_HORIZONTAL_LAYERED_DIGRAPH_LAYOUT]:
        'Horizontal Tree',
    },
    aiMappingAssistLabel: 'AI mapping assist (Recommended)',
    aiMappingAssistHelperText:
      'Mappings are suggested by Voicebox. Turn off to map manually.',
    prefixTable: {
      dataModel: 'Data Model',
      namespace: 'Namespace',
      namespacePlaceholder: 'Enter a new namespace',
      prefix: 'Prefix',
      prefixPlaceholder: 'Enter a new prefix',
      setPrefix: 'Set Prefix',
    },
    aiHints: {
      columnHeader: 'AI Hints',
      tooltip: 'Configure AI hints',
      dialogTitle: (modelName: string) => `AI Hints - ${modelName}`,
      description:
        'Provide additional context and guidance for Voicebox. Hints describe domain-specific terminology, conventions, and common business logic that may not be explicit in the data model.',
      examplesText:
        'Examples:\nRemember to appropriately scale quantities as shown based on their units.\n---\nprofit margin = (revenue - cost) / revenue\n---\nIf there are no tariffs for a country, the tariff rate is 0% and the exposure is 0 $/gal.\nThis requires the use of OPTIONAL when retrieving tariffs on materials to correctly reflect when materials do not have tariffs against them by a country.',
      placeholder: 'Enter AI hints for this data model ....',
      save: 'Save',
      cancel: 'Cancel',
    },
    validation: {
      nameRequired: 'Project name is required',
      namespaceRequired: 'Namespace is required',
      namespaceStartsWith:
        'The IRI must start with an alphabet character ([A-Za-z]).',
      namespaceSemicolon: 'The IRI must include a semicolon ([:]).',
      namespaceChars: (chars: string[]) =>
        `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
      prefixAlreadyExists: 'Prefixes must be unique',
      prefixRequired: 'Prefix is required',
    },
  },
  publishProjectDrawer: {
    title: 'Publish Project to Stardog',
    newTitle: 'Publish Project',
    connectionDetails: {
      title: 'Endpoint Details',
      help: 'Confirm the Stardog endpoint and database you want to publish your project to.',
    },
    databaseConfiguration: {
      databaseSectionHeader: 'Database',
      header: 'Database Properties',
      namespaceSection: {
        header: 'Namespaces',
        iri: 'IRI',
        iriFieldWarning:
          'Published namespaces allow users to abbreviate IRIs by using a prefix. The suggested IRI is based on the data model namespace for your project. Changing the IRI could reduce the relevancy of the published prefix.',
        overwriteWarningMessage: (prefix: string, existingIri: string) => (
          <>
            The{' '}
            <Tag intent={Intent.WARNING} minimal>
              {prefix}
            </Tag>{' '}
            prefix is already associated with the{' '}
            <Tag intent={Intent.WARNING} minimal>
              {existingIri}
            </Tag>{' '}
            IRI in the selected database. Proceeding with publish will replace
            the current namespace.
          </>
        ),
        overwriteWarningTitle: 'Existing namespace will be overwritten',
        prefix: 'Prefix',
        prefixEmpty: '<empty>',
      },
      voiceboxSwitch: {
        label: 'Enable Voicebox',
        limitReached:
          'Voicebox is already enabled for the maximum number of databases your Stardog endpoint license supports.',
        questions: 'Publish questions as spotlight questions',
        evaluateTooltip: 'Re-evaluate questions',
      },
    },
    mappingConfiguration: {
      header: 'Configure Mappings',
      columnHeader: {
        resource: 'Resource',
        mappings: 'Mappings',
        configuration: 'Configuration',
      },
      shouldPublish: 'Publish',
      virtualGraph: {
        label: 'Virtual graph',
        create: 'Create new',
        replace: 'Replace',
        select: 'Select an existing virtual graph',
        placeholder: 'Select a virtual graph',
        staticIriWarning:
          'This mapping contains patterns only available for endpoints running Stardog 11 or later.',
        versionWarning:
          'Resource mappings for Stardog Knowledge Graph endpoints of higher versions than the Stardog endpoint version being published may not be compatible.',
        insufficientPrivilegesVirtualGraphs:
          'Insufficient privileges to publish virtual graphs.',
      },
      namedGraph: {
        append: 'Append',
        label: 'Named graph',
        create: 'Create new',
        replace: 'Replace',
        placeholder: 'Select a named graph',
      },
      csvUpload: {
        label: 'Re-upload large CSV resource',
        info: `Designer only stores files under ${
          MAX_CSV_UPLOAD_BYTES / 1024 / 1024
        } MB. To publish your project, re-upload this resource.`,
      },
    },
    namespace: {
      columnHeader: {
        namespaceIri: 'Namespace IRI',
        prefix: 'Prefix',
      },
      validation: {
        namespaceRequired: 'Namespace is required',
        namespaceStartsWith:
          'The IRI must start with an alphabet character ([A-Za-z]).',
        namespaceSemicolon: 'The IRI must include a semicolon ([:]).',
        namespaceChars: (chars: string[]) =>
          `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
        prefixExistsOnDatabase: 'Prefix exists on the database',
        prefixMustBeUnique: 'Prefixes must be unique',
        prefixRequired: 'Prefix is required',
      },
    },
    modelAlias: {
      columnHeader: {
        models: 'Data Model(s)',
        modelNamedGraph: 'Data model named graph',
      },
      includedModelNamedGraphs: 'Included data model named graphs',
      label: 'Data Model Combo',
      labelHelp:
        'A data model combo allows you to reference one or more data models using a single identifier.',
    },
    modelConfiguration: {
      columnHeader: {
        publish: 'Publish',
        model: 'Data Model',
      },
      header: 'Configure Data Models',
      modelConfiguration: 'Data Model Configuration',
      modelSelection: {
        consolidate: 'Consolidate to a single data model',
        description: 'Select how to publish data models',
        multiple: 'Multiple data models',
        publishModel: 'Publish Data Model',
        title: 'Data Model Selection',
      },
      modelName: {
        create: 'Create a new data model',
        exampleText: 'Data Model Name',
        label: 'Data model name',
        noExisting:
          'There are no existing data models to replace when creating a database.',
        noModelsToReplace:
          'There are no existing data models to replace in this database.',
        placeholder: 'Select a data model',
        replace: 'Replace an existing data model',
        select: 'Select an existing data model',
      },
      namedGraph: {
        exampleText: 'urn:model:example',
        label: 'Named graph',
      },
      rules: {
        label: 'Publish Inference Rules',
        incompleteRulesWarning: 'Incomplete rules will not be published.',
      },
      sensitivePropertyGroups: {
        label: 'Sensitive Properties',
        groupLabel: 'Sensitive Property Group',
        configureLabel: 'Configure',
        new: 'Create new',
        replace: 'Replace',
        append: 'Append',
        existingPlaceholder: 'Select an existing group',
        newPlaceholder: 'Enter a new group name',
        noExisting: 'There are no existing property groups.',
        duplicateGroup: (group: string) =>
          `The group ${group} already exists for your database. Please enter a unique name.`,
        duplicatePublishGroup: (group: string) =>
          `The group ${group} is already being published. Please enter a unique name.`,
        replaceOrAppendUnnamedGroup: (group: string) =>
          `The group ${group} needs to be assigned an existing sensitive property group.`,
      },
    },
    openInExplorer: {
      explorerCard: {
        description:
          'An intuitive interface to visually browse and query your Knowledge Graph.',
        label: 'EXPLORER',
        title:
          'Ready to see your knowledge graph in action? Open your published work in Explorer.',
      },
      voiceboxCard: {
        description: 'A simpler way to ask questions of your data.',
        label: 'VOICEBOX',
        title:
          'Ready to ask natural language questions? Open your published work in Voicebox.',
      },
      close: 'Close',
      success: 'Success!',
      successMessage: 'Your project was successfully published.',
    },
    submit: 'Publish',
    upload: {
      drag: 'Drag files to upload',
      label: 'Confirm and upload project files',
    },
    willDelete: ({
      modelName,
      namedGraphIri,
    }: {
      modelName: string;
      namedGraphIri: string;
    }) => `Replacing ${modelName} will delete the named graph ${namedGraphIri}`,
    validation: {
      connection: 'Endpoint is required',
      database: 'Database is required',
      evaluateQuestions: 'Re-evaluate questions to publish.',
      refreshDatabase: 'Refreshing database...',
      modelName: {
        required: 'Data model name is required',
        startsWith: 'Data model name must start with [A-Za-z].',
        characters: (chars: string[]) =>
          `The data model name is invalid. "${chars.join(
            ','
          )}" is not allowed.`,
        replacingPublishingModel:
          'This model is already being published and cannot be replaced.',
        unique:
          'This model already exists. Please enter a unique data model name.',
        usedIri:
          'This iri overlaps with another data model that will be created',
        usedName:
          'This name overlaps with another data model that will be created',
      },
      modelNames: {
        missingOrInvalidNames:
          'There are data models with missing or invalid names',
        missingOrInvalidNamedGraphs:
          'There are data models with missing or invalid named graphs',
        overlappingNames: 'There are data models with overlapping names',
        overlappingNamedGraphs:
          'There are data models with overlapping named graphs',
      },
      namespace: 'Namespace is invalid',
      noModelsSelected: 'No data models selected',
      duplicatePublishNamespacePrefix: (prefix: string) =>
        `The namespace prefix ${prefix} is already being published. Please enter a unique name.`,
      upload: 'All files must be uploaded',
      namedGraph: {
        required: 'Named graph is required.',
        startsWith:
          'The named graph must start with an alphabet character ([A-Za-z]).',
        semicolon: 'The named graph must include a semicolon ([:]).',
        characters: (chars: string[]) =>
          `The named graph is invalid. "${chars.join(',')}" is not allowed.`,
        unique:
          'This named graph already exists. Please enter a unique named graph.',
      },
      csvUpload: {
        incomplete: (name: string, message: string) =>
          `"${name}" is invalid: ${message}`,
        required: 'File upload is required.',
      },
      virtualGraph: {
        incomplete: (name: string, message: string) =>
          `"${name}" is invalid: ${message}`,
        required: 'Virtual graph name is required.',
        startsWith:
          'The virtual graph name must start with an alphabet character ([A-Za-z]).',
        characters: (chars: string[]) =>
          `The virtual graph name is invalid. "${chars.join(
            ','
          )}" is not allowed.`,
        unique:
          'This virtual graph already exists. Please enter a unique virtual graph name.',
      },
    },
    footer: {
      exportProject: 'Download zipped project when publishing',
      next: 'Next',
      publish: 'Publish',
      back: 'Back',
      cancel: 'Cancel',
    },
  },
  resourceSelector: {
    placeholder: 'Select resource(s)...',
  },
  resourceSuggestionsDialog: {
    footer: {
      back: 'Back',
      cancel: 'Cancel',
      create: 'Create',
      next: 'Next',
    },
    selection: {
      classesHelperTextVoicebox:
        'Leave blank to have Voicebox generate data model concepts.',
      classesLabel: 'Select classes to map to',
      invalid: 'A resource must be selected to continue',
      invalidManual: 'A resource and a class must be selected to continue',
      noSuggestions: (hasIgnored: boolean) =>
        `There were no suggestions found.${
          hasIgnored ? ' Suggestions for existing mappings were ignored.' : ''
        } To manually create a mapping select a single resource and class.`,
      resourcesHelperTextVoicebox:
        'Leave blank to have Voicebox generate mock data.',
      resourcesLabel: 'Select resources to map from',
      title: 'New Mapping',
    },
    generatingMockDataLoading: {
      title: 'Building your data model and generating mock data',
      subtitle: 'This may take several minutes.',
    },
  },
  signInDialog: {
    title: 'Sign in',
    submit: 'Connect',
    form: {
      username: 'Username',
      password: 'Password',
      passwordRequired: 'Password is required',
    },
  },
  virtualGraphPreviewDialog: {
    advancedOptions: {
      title: (count: number) => `Advanced Options (${count})`,
      propertyHeader: 'Property',
      valueHeader: 'Value',
      propertyName: 'Property name',
      propertyValue: 'Property value',
      propertySuggestions: 'Property suggestions',
      valueSuggestions: 'Value suggestions',
      booleanTrue: 'true',
      booleanFalse: 'false',
      addOption: 'Add option',
      selectProperty: 'Select property',
      selectValue: 'Select value',
    },
    metadataRefreshFailed:
      'Failed to refresh table metadata. Please try again.',
    noDataSource: 'No data source',
    noTableSelected: {
      title: 'You have no tables selected.',
      subtitle: 'Select a table to see a preview.',
    },
    noCustomQuery: {
      title: 'Write a SQL query to get started',
      subtitle: 'Paste or write a SQL query on the left, then click Run.',
    },
    errorTitle: 'Error',
    noTablesFound: 'No tables found',
    queryPlaceholder: 'Paste query',
    replaceDataSourceButton: 'Replace',
    resourceNamePlaceholder: 'Resource name',
    revertButton: 'Revert',
    saveButton: 'Save',
    refreshMetadataTooltip:
      'Refresh Data Source metadata. This may take a long time.',
    searchPlaceholder: 'Search',
    sqlQuery: 'SQL Query',
    sqlQueryTab: 'SQL query',
    tableSelection: 'Table selection',
    title: 'Virtual Graph Preview',
    warningTitle:
      'Editing a project resource may result in the removal of mappings which are no longer valid',
    nextButton: 'Next',
    dataSourceTypeSelection: {
      title: 'Replace resource',
      subtitle: 'Connect to a data source',
    },
    connectionSelection: {
      title: 'Select connection',
      subtitle: 'Use an existing connection or create a new one',
      newConnection: 'New connection',
      connect: 'Connect',
      sample: 'Sample datasets',
    },
    dataSourceCreation: {
      title: (type: string) => `Connect to ${type}`,
      connectionCredentials: 'Connection credentials',
      connectionName: 'Connection name',
      endpoint: 'Endpoint',
      username: 'Username',
      password: 'Password',
      connecting: 'Connecting',
      connect: 'Connect',
    },
  },
  voicebox: {
    title: 'Voicebox',
    disabledFor: {
      [ProjectSidebarTileId.MODELS]:
        'To add data models, please first accept or discard the changes to your project.',
      [ProjectSidebarTileId.RESOURCES]:
        'To add resources, please first accept or discard the changes to your project.',
      settings:
        'To manage settings, please first accept or discard the changes to your project.',
      export:
        'To export, please first accept or discard the changes to your project.',
      publish:
        'To publish, please first accept or discard the changes to your project.',
    },
    drawer: {
      description:
        'Stardog Voicebox for Designer makes modifying your data model as simple as asking a question. Go ahead and ask Voicebox to create a new class, change the label of a relationship, or add more attributes to a class.\n\nThe prompts below are examples and may not be applicable to your project.',
      examples: [
        'Create an ontology for a retail store knowledge graph that includes concepts for customer, product, and purchase',
        'Add the concept address',
        'Customers have billing addresses and purchases have shipping addresses',
        'Delete the relationship has address',
        'Add a more detailed description to customer based on its relationships and attributes',
        'Delete product and all associated relationships',
      ],
    },
    footer: {
      accept: 'Accept Changes',
      help: 'You are viewing a preview of suggested mappings and data model changes.',
      reject: 'Discard',
    },
    history: {
      accepted: 'Accepted changes',
      rejected: 'Discarded changes',
      suggestions: 'Added suggested mappings.',
    },
    legend: {
      changes: 'Changes',
      deletions: 'Deletions',
    },
    summary: {
      actions: {
        addDataset: 'Add a resource',
        addQuestions: 'Add more questions',
        groups: {
          unansweredQuestions: 'Unanswered questions',
          unusedResources: 'Unused resources',
          unlinkedConcepts: 'Unused data model concepts',
          addQuestions: 'More questions recommended',
          addDataset: 'More resources recommended',
        },
        more: (count: number) => `More (${count})`,
      },
      evaluate: 'Refresh',
      title: 'Model Summary',
    },
  },
  webProtocolWarning: {
    httpToHttps: () => (
      <>
        The Stardog Knowledge Graph endpoint is not responding. This could be
        because:
        <ul>
          <li>Your Knowledge Graph endpoint is not running</li>
          <li>
            You are connecting to a HTTPS endpoint from a website without SSL
          </li>
        </ul>
        Confirm your Knowledge Graph endpoint is running and you have the
        correct URL.
      </>
    ),
    httpsToHttp: () => (
      <>
        The Stardog Knowledge Graph endpoint is not responding. This could be
        because:
        <ul>
          <li>Your Knowledge Graph endpoint is not running</li>
          <li>
            You are connecting to a HTTP endpoint from an SSL enabled website
          </li>
        </ul>
        Confirm your Knowledge Graph endpoint is running and you have the
        correct URL.{' '}
        <a href={SSL_SETUP_DOCUMENTATION_LINK} rel="noreferrer" target="_blank">
          Click here
        </a>{' '}
        to learn how to enable SSL for Stardog.
      </>
    ),
    httpToLocal: () => (
      <>
        The Stardog Knowledge Graph endpoint is not responding. This could be
        because:
        <ul>
          <li>Your Knowledge Graph endpoint is not running</li>
          <li>
            You are connecting to a local endpoint from a website without SSL
          </li>
        </ul>
        Confirm your Knowledge Graph endpoint is running and you have the
        correct URL.
      </>
    ),
  },
};

export const newline = '\n';

export const notifications = {
  defaults: {
    failure:
      "Something went wrong and we're not sure what it is. Contact your system admin to review Stardog Knowledge Graph logs for more details if the issue persists.",
    forbidden: (action: string) =>
      `You are not authorized to ${action}. Contact your system admin to ensure you have the correct user privileges.`,
    maxFileSizeExceeded: `File size exceeded. Only files under ${MAX_FILE_SIZE_IN_MB}MB can be uploaded with Designer.`,
    refreshProjectModelFailed:
      'Failed to refresh data model. Please try again.',
    refreshProjectModelNoChanges: 'Data model is up to date.',
    undo: 'Undo',
  },
  validateConnection: {
    success: "You've connected to the Stardog endpoint!",
    failure: {
      default:
        'The Stardog Knowledge Graph endpoint is not responding. Please check your server status or endpoint.',
      refused:
        'The connection was refused. Make sure your Stardog endpoint is online and your endpoint is correct.',
      disconnected:
        'Oops! You were disconnected from your Stardog endpoint. Try connecting again.',
      invalid: 'Your username and/or password are incorrect.',
      aborted:
        'The request was aborted. Please check the status of your Stardog endpoint.',
    },
  },
  csv: {
    mismatchedHeaders: (filename: string) =>
      `The headers in ${filename} do not match those of the original file`,
    partial:
      'Storage limit reached. CSV will not be saved and must be re-uploaded when publishing.',
  },
  database: {
    addModelFile: {
      actionDescription: 'add model file',
      failure: 'Failed to add model to database',
      success: `Add model to database.`,
    },
    addData: 'add data',
    beginTransaction: 'begin a transaction',
    clearNamedGraph: 'clear named graph',
    createDatabase: 'create a database',
    createDatabaseFailure: 'Failed to create database',
    commitTransaction: 'commit a transaction',
    importCsv: 'import csv files',
    importNamespaces: 'import database namespaces',
    publishDatabaseNamespaces: 'publish database namespaces',
    publishSpgs: 'publish sensitive property groups',
    updateDatabaseDetails: 'update database metadata',
  },
  dataSources: {
    creation: {
      actionDescription: 'create data source',
      failure: 'Failed to create data source',
      success: (name: string) => `Created data source ${name}.`,
    },
    suggestions: {
      actionDescription: 'request mapping suggestions',
      failure: 'Failed to request suggestions',
      noneFound: 'No suggested mappings were found.',
    },
    metadataRefresh: {
      actionDescription: 'refresh data source metadata',
      success: 'Refreshed data source metadata',
      failure: 'Failed to refresh data source metadata',
    },
    requestDetails: 'request data source details',
    update: 'update data source',
  },
  importModel: {
    unsupported: () => (
      <>
        Unsupported model criteria were skipped. Review{' '}
        <a
          href={DESIGNER_ARTIFACT_DEFINITIONS_LINK}
          rel="noreferrer"
          target="_blank"
        >
          documentation
        </a>{' '}
        for supported criteria.
      </>
    ),
  },
  project: {
    exported: (filename: string) => `Exported project to ${filename}`,
    exportFailure: 'Project failed to be exported.',
    importFromFile: {
      invalid: 'Project failed to be imported.',
    },
    removed: (name: string) => `The project ${name} was deleted`,
    restored: (name: string) => `The project ${name} was restored`,
    openedMissing: (name: string | undefined) =>
      name ? `Project ${name} not found!` : 'Project not found!',
    storedOnCloud: (name: string, isLaunchpad: boolean) =>
      `The project ${name} was promoted to ${
        isLaunchpad ? `account` : `cloud`
      } storage`,
    storedLocally: (name: string) => `The project ${name} was stored locally`,
    duplicatedCloudBackupToLocalProject: (name: string) =>
      `The project ${name} was duplicated to local storage`,
    backedUpCloudProjectToLocal: (name: string) => (
      <>
        <strong>{name}</strong> was saved to local storage due to a connection
        error.
      </>
    ),
    migratedLocalProjectBackupToCloud: (name: string, isLaunchpad: boolean) => (
      <>
        <strong>{name}</strong> synced to {isLaunchpad ? `account` : `cloud`}{' '}
        storage.
      </>
    ),
  },
  publishProject: {
    competencyQuestions: {
      actionDescription: 'publish competency question',
      success: (question: string) =>
        `Published competency question: ${question}`,
      successAll: 'Published competency questions',
      failure: (question: string) =>
        `Failed to publish competency question: ${question}`,
      duplicateVoiceboxQuestion: (question: string) =>
        `No spotlight question was published for “${question}” because a duplicate question already exists.`,
    },
    databaseNamespaces: {
      success: 'Updated database namespaces',
      failure: 'Failed to update database namespaces',
      refreshFailure:
        'Skipped publishing database namespaces because the latest namespaces could not be retrieved from the database',
    },
    model: {
      create: {
        success: (name: string) => `Created data model ${name}`,
        failure: 'Failed to create data model',
      },
      replace: {
        success: (name: string) => `Replaced data model ${name}`,
        failure: 'Failed to replace data model',
      },
      updateReasoningSchemas: {
        actionDescription: 'update database metadata',
        success: 'Published data model(s)',
        failure: 'Failed to publish data model(s)',
      },
    },
    namedGraph: {
      clearFailure: 'Failed to clear named graph(s)',
    },
    namespaces: {
      success: 'Added data model namespaces',
      failure: 'Failed to add data model namespaces',
    },
    rules: {
      success: (modelName: string) =>
        `Added inferencing rules to data model ${modelName}`,
      failure: (modelName: string) =>
        `Failed to add inferencing rules to data model ${modelName}`,
    },
    sensitivePropertyGroups: {
      success: 'Updated sensitive property groups',
      failure: 'Failed to update sensitive property groups',
    },
    updateUniqueKeySets: {
      success: (dataSourceId: string) =>
        `Updated unique key sets for data source ${dataSourceId}`,
      failure: (dataSourceId: string) =>
        `Failed to update unique key sets for data source ${dataSourceId}`,
    },
    virtualGraph: {
      create: {
        success: (name: string) => `Created virtual graph ${name}`,
        failure: (name: string) => `Failed to create virtual graph ${name}`,
      },
      replace: {
        success: (name: string) => `Replaced virtual graph ${name}`,
        failure: (name: string) => `Failed to replace virtual graph ${name}`,
      },
    },
    importCsv: {
      success: (name: string) => `Imported CSV ${name}`,
      failure: (name: string) => `Failed to import ${name}`,
    },
    fileUploadMetadata: {
      success: 'Added file upload metadata',
      failure: 'Failed to add file upload metadata',
    },
    success: (name: string) => `Published ${name}`,
    partial: (name: string) => `Problems publishing ${name}`,
    failure: (name: string) => `Failed to publish ${name}`,
    permissionDenied: (action: string, resourceType: string) =>
      `Publish failed: insufficient privileges to ${action} ${resourceType}.`,
  },
  rules: {
    saved: (name: string) => `Saved rule ${name}`,
  },
  voicebox: {
    generateQuery:
      'Could not generate a supporting query for the question. Question details are still available.',
    generateQueries:
      'Could not generate supporting queries for the questions. Question details are still available.',
    generateQuestions:
      'Could not generate competency questions. Try again or add questions manually.',
    generateScore: 'Could not evaluate question. Please try again.',
    generateScores: 'Could not evaluate questions. Please try again.',
    generateSummary: 'Could not generate model summary. Please try again.',
    generateMappings: 'Could not generate suggestions. Please try again.',
  },
  collaboration: {
    authFailed: 'Collaboration unavailable — saving locally.',
  },
};

export const requests = {
  noConnection:
    'No connection passed in action or default connection in state not yet setup',
  operationFailed: 'Operation failed.',
};
